import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  constructor() {
    super();
    this.setupOrderNoteListener();
    this.setupCartPageRepositioning();
  }

  setupCartPageRepositioning() {
    if (!this.isCartPage()) return;
    setTimeout(() => this.repositionInCartPage(), 50);
  }

  isCartPage() {
    const pathname = window.location.pathname;
    return pathname === '/cart' || pathname.endsWith('/cart');
  }

  repositionInCartPage() {

    if (!this.isCartPage()) {
      return;
    }
    
    if (!document.contains(this)) {
      return;
    }
    
    const cartNotesWrapper = document.querySelector('.cart__footer-order-note-wrapper');
    const cartFooterInner = document.querySelector('.cart__footer-inner');
    
    if (!cartFooterInner || !this.parentNode) {
      return;
    }
    
    if (cartNotesWrapper) {
      const nextSibling = this.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('cart__footer-order-note-wrapper')) {
        return;
      }
      
      const existingDiscountBeforeNotes = cartNotesWrapper.previousElementSibling;
      if (existingDiscountBeforeNotes && existingDiscountBeforeNotes.tagName === 'CART-DISCOUNT-COMPONENT' && existingDiscountBeforeNotes !== this) {
        return;
      }
      
      try {
        cartNotesWrapper.parentNode.insertBefore(this, cartNotesWrapper);
      } catch (error) {
        console.warn('Could not reposition cart discount:', error);
      }
    } else {
      const firstChild = cartFooterInner.firstElementChild;
      if (firstChild && firstChild.tagName === 'CART-DISCOUNT-COMPONENT') {
        return;
      }
      
      try {
        cartFooterInner.insertBefore(this, firstChild);
      } catch (error) {
        console.warn('Could not reposition cart discount:', error);
      }
    }
  }

  setupOrderNoteListener() {
    const orderNoteInput = document.querySelector('.quick-cart__footer-order-note-input');
    if (orderNoteInput) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-expanded') {
            const isExpanded = orderNoteInput.getAttribute('aria-expanded') === 'true';
            const isMobile = window.innerWidth <= 480;
            if (isMobile) {
              this.style.bottom = isExpanded ? '326px' : '198px';
            } else {
              this.style.bottom = isExpanded ? '351px' : '203px';
            }
          }
        });
      });

      observer.observe(orderNoteInput, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }
  }

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          if (discountCodeValue.toUpperCase() === 'FREEDELIVERY') {
            const countryCode = window.shopifyCountry || '';
            if (countryCode === 'CZ' || countryCode === 'SK') {
              sessionStorage.setItem('freedelivery_applied', 'true');
              
              const event = new CustomEvent('freedelivery:applied');
              document.dispatchEvent(event);
              
              discountCode.value = '';
              return;
            }
          }
          
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      if (!this.isCartPage()) {
        const quickCartWrapper = document.querySelector('.quick-cart__wrapper');
        if (quickCartWrapper) {
          quickCartWrapper.classList.add('active');
        }
      }

      morphSection(this.dataset.sectionId, newHtml);
      
      const cartEvent = new CustomEvent('cart:updated', {
        detail: { cart: data },
        bubbles: true
      });
      document.dispatchEvent(cartEvent);
      
      if (!this.isCartPage()) {
        sessionStorage.setItem('openQuickCartAfterReload', '1');
      }
      window.location.reload();
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;
    
    if (discountCode.toUpperCase() === 'FREEDELIVERY') {
      sessionStorage.removeItem('freedelivery_applied');
      const event = new CustomEvent('freedelivery:removed');
      document.dispatchEvent(event);
    }

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Keep the quick cart open by maintaining its active class (only if not on cart page)
      if (!this.isCartPage()) {
        const quickCartWrapper = document.querySelector('.quick-cart__wrapper');
        if (quickCartWrapper) {
          quickCartWrapper.classList.add('active');
        }
      }

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      
      const cartEvent = new CustomEvent('cart:updated', {
        detail: { cart: data },
        bubbles: true
      });
      document.dispatchEvent(cartEvent);
      
      if (!this.isCartPage()) {
        sessionStorage.setItem('openQuickCartAfterReload', '1');
      }
      window.location.reload();
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

function isOnCartPage() {
  const pathname = window.location.pathname;
  return pathname === '/cart' || pathname.endsWith('/cart');
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

document.addEventListener('cart:updated', () => {
  if (isOnCartPage()) {
    setTimeout(() => {
      const cartDiscountComponent = document.querySelector('cart-discount-component');
      if (cartDiscountComponent && cartDiscountComponent.repositionInCartPage) {
        cartDiscountComponent.repositionInCartPage();
      }
    }, 150);
  }
});

if (isOnCartPage()) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && 
          mutation.target.matches('.cart__container, [data-section-type="cart"]') &&
          mutation.addedNodes.length > 0) {
        let attempts = 0;
        const maxAttempts = 3;
        
        const tryReposition = () => {
          attempts++;
          const cartDiscountComponent = document.querySelector('cart-discount-component');
          
          if (cartDiscountComponent && cartDiscountComponent.repositionInCartPage) {
            cartDiscountComponent.repositionInCartPage();
            return true;
          } else if (attempts < maxAttempts) {
            setTimeout(tryReposition, attempts * 100);
          } else {
            console.log('Failed to find cart-discount component after', maxAttempts, 'attempts');
          }
        };
        
        tryReposition();
      }
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    const cartContainer = document.querySelector('.cart__container, [data-section-type="cart"]');
    if (cartContainer) {
      observer.observe(cartContainer, {
        childList: true,
        subtree: false
      });
    }
  });
  
  window.addEventListener('beforeunload', () => {
    if (observer) {
      observer.disconnect();
    }
  });
}
